import React, { useState, useEffect } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, Avatar,
  List, ListItem, ListItemIcon, ListItemText, ListItemButton,
  Divider, Chip, Tooltip, Menu, MenuItem, Badge, useMediaQuery,
  useTheme, Fab,
} from '@mui/material';
import {
  Menu as MenuIcon, PeopleAlt, AccountBalance, EventAvailable,
  Stars, Settings, Logout, Notifications, DashboardCustomize,
  ChevronLeft, KeyboardArrowDown, SmartToy, MenuBook, LockReset,
} from '@mui/icons-material';
import { useNavigate }        from 'react-router-dom';
import { useAuth }            from '../context/AuthContext';
import logo                   from '../assets/logo.png';
import hexarLogo              from '../assets/hexar_image.PNG';
import AISummaryBar           from '../components/AISummaryBar';
import AlertsPanel            from '../components/AlertsPanel';
import TabAISummary           from '../components/TabAISummary';
import DarkModeToggle         from '../components/DarkModeToggle';
import ChatbotDrawer          from '../components/ChatbotDrawer';
import ChangePasswordDialog   from '../components/ChangePasswordDialog';
import ChatContext            from '../context/ChatContext';
import WorkforcePulseTab      from './WorkforcePulseTab';
import FinanceBudgetTab       from './FinanceBudgetTab';
import OperationsTab          from './OperationsTab';
import TalentPerformanceTab   from './TalentPerformanceTab';
import SettingsPage           from './SettingsPage';
import KbManagementPage       from './KbManagementPage';   // Phase 5A

const DRAWER_WIDTH = 240;

const ALL_TABS = [
  { key: 'workforce-pulse',    label: 'Workforce Pulse',      sublabel: 'Headcount & Demographics', icon: PeopleAlt,      color: '#1565C0', component: WorkforcePulseTab },
  { key: 'finance-budget',     label: 'Finance & Budget',     sublabel: 'Payroll & Claims',          icon: AccountBalance, color: '#00695C', component: FinanceBudgetTab },
  { key: 'operations',         label: 'Operations',           sublabel: 'Absence & Attendance',      icon: EventAvailable, color: '#37474F', component: OperationsTab },
  { key: 'talent-performance', label: 'Talent & Performance', sublabel: 'Career & Training',         icon: Stars,          color: '#AD1457', component: TalentPerformanceTab },
];

// Admin sidebar items — rendered under the ADMINISTRATION section
const ADMIN_ITEMS = [
  {
    key:    'settings',
    label:  'User Access Control',
    icon:   Settings,
    color:  { active: '#CE93D8', bg: 'rgba(106,27,154,0.2)', border: 'rgba(106,27,154,0.4)', hover: 'rgba(106,27,154,0.1)' },
  },
  {
    key:    'kb-management',
    label:  'Knowledge Base',
    icon:   MenuBook,
    color:  { active: '#80CBC4', bg: 'rgba(0,105,92,0.2)',   border: 'rgba(0,105,92,0.4)',   hover: 'rgba(0,105,92,0.1)' },
  },
];

export default function MainDashboard() {
  const { user, getAccessibleTabs, isAdmin, isSuperUser } = useAuth();
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));

  const accessibleTabKeys = getAccessibleTabs();
  const visibleTabs       = ALL_TABS.filter((t) => accessibleTabKeys.includes(t.key));
  const defaultTab        = isAdmin() && !isSuperUser() ? 'settings' : (visibleTabs[0]?.key || null);

  const canAccessTab = (key) =>
    (key === 'settings' || key === 'kb-management')
      ? (isAdmin() || isSuperUser())
      : accessibleTabKeys.includes(key);

  const [activeTab,  setActiveTab]  = useState(defaultTab);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl,   setAnchorEl]   = useState(null);
  const [chatOpen,   setChatOpen]   = useState(false);
  const [pwdOpen,    setPwdOpen]    = useState(false);
  const [aiPrompt,   setAiPrompt]   = useState(null);
  const [now,        setNow]        = useState(new Date());

  const openWithMessage = (msg) => { setAiPrompt(msg); setChatOpen(true); };
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = () => { navigate('/logout'); };
  
  const renderContent = () => {
    if (!canAccessTab(activeTab)) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You don't have access to this page.
          </Typography>
        </Box>
      );
    }
    if (activeTab === 'settings')      return <SettingsPage />;
    if (activeTab === 'kb-management') return <KbManagementPage />;   // Phase 5A
    const tab = ALL_TABS.find((t) => t.key === activeTab);
    if (!tab) return null;
    const Component = tab.component;
    return <Component />;
  };

  const activeTabInfo = ALL_TABS.find((t) => t.key === activeTab);

  // Breadcrumb label for admin pages
  const getBreadcrumb = () => {
    if (activeTab === 'settings')      return 'User Access Control';
    if (activeTab === 'kb-management') return 'Knowledge Base';
    return activeTabInfo?.label ?? 'Dashboard';
  };

  const NavDrawer = (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sx={{
        width: drawerOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', bgcolor: '#0A1628', color: '#fff', border: 'none' },
      }}
    >
      {/* Brand */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src={hexarLogo}
          alt="HRMS AI Dashboard logo"
          sx={{ width: 204, height: 80, borderRadius: '10px', flexShrink: 0, objectFit: 'cover' }}
        />
        {isMobile && (
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ ml: 'auto', color: 'rgba(255,255,255,0.5)' }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* User info */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: '#1565C0', fontWeight: 700, fontSize: '0.85rem' }}>{user?.avatar}</Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} color="#fff" noWrap>{user?.name}</Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.5)" noWrap>{user?.department}</Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />

      {/* Dashboard tabs */}
      <Typography variant="caption" sx={{ px: 3, pt: 2, pb: 0.5, color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: '0.8px', display: 'block' }}>
        DASHBOARD TABS
      </Typography>

      <List sx={{ px: 1.5 }}>
        {visibleTabs.map((tab) => {
          const Icon     = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <ListItem key={tab.key} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { setActiveTab(tab.key); if (isMobile) setDrawerOpen(false); }}
                sx={{
                  borderRadius: 2, px: 1.5, py: 1,
                  bgcolor:   isActive ? `${tab.color}28` : 'transparent',
                  border:    isActive ? `1px solid ${tab.color}50` : '1px solid transparent',
                  '&:hover': { bgcolor: `${tab.color}18` },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon sx={{ fontSize: 20, color: isActive ? tab.color : 'rgba(255,255,255,0.45)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={tab.label}
                  secondary={tab.sublabel}
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,0.7)' }}
                  secondaryTypographyProps={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Administration section — Settings + KB Management */}
      {(isSuperUser() || isAdmin()) && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, my: 1 }} />
          <Typography variant="caption" sx={{ px: 3, pb: 0.5, color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: '0.8px', display: 'block' }}>
            ADMINISTRATION
          </Typography>
          <List sx={{ px: 1.5 }}>
            {ADMIN_ITEMS.map((item) => {
              const Icon     = item.icon;
              const isActive = activeTab === item.key;
              const c        = item.color;
              return (
                <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => { setActiveTab(item.key); if (isMobile) setDrawerOpen(false); }}
                    sx={{
                      borderRadius: 2, px: 1.5, py: 1,
                      bgcolor:   isActive ? c.bg    : 'transparent',
                      border:    isActive ? `1px solid ${c.border}` : '1px solid transparent',
                      '&:hover': { bgcolor: c.hover },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Icon sx={{ fontSize: 20, color: isActive ? c.active : 'rgba(255,255,255,0.45)' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,0.7)' }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {/* Logout */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1 }} />
        <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, px: 1.5, py: 1, '&:hover': { bgcolor: 'rgba(239,83,80,0.12)' } }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Logout sx={{ fontSize: 20, color: '#EF9A9A' }} />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: '#EF9A9A' }} />
        </ListItemButton>
      </Box>
    </Drawer>
  );

  return (
    <ChatContext.Provider value={{ openWithMessage }}>
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      {NavDrawer}

      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'margin 0.2s' }}>
        <AppBar position="static" elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: '1px solid rgba(21,101,192,0.1)', color: 'text.primary' }}>
          <Toolbar sx={{ gap: 1.5, position: 'relative' }}>
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)} edge="start">
              <MenuIcon />
            </IconButton>

            {!drawerOpen && (
              <Box
                component="img"
                src={logo}
                alt="HRMS AI Dashboard logo"
                sx={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
              />
            )}

            {/* Left: HRMS label + live clock */}
            <Box>
              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">HRMS AI Dashboard</Typography>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {now.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
                  &nbsp;&nbsp;
                  {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </Typography>
              </Box>
            </Box>

            {/* Center: page title — absolutely positioned */}
            <Box sx={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
                {getBreadcrumb()}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <AlertsPanel />
            <DarkModeToggle />

<Tooltip title={user?.name}>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#1565C0', fontSize: '0.8rem', fontWeight: 700 }}>{user?.avatar}</Avatar>
                <KeyboardArrowDown fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>{user?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); setPwdOpen(true); }} sx={{ gap: 1 }}>
                <LockReset fontSize="small" /> Change Password
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main', gap: 1 }}>
                <Logout fontSize="small" /> Logout
              </MenuItem>
            </Menu>

            <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
          {/* AI Summary Bar hidden on admin pages */}
          {activeTab !== 'settings' && activeTab !== 'kb-management' && <AISummaryBar />}
          {activeTab !== 'settings' && activeTab !== 'kb-management' && <TabAISummary key={activeTab} tabKey={activeTab} />}
          {renderContent()}
        </Box>
      </Box>

      {/* Chatbot FAB — hidden on admin pages */}
      {activeTab !== 'settings' && !chatOpen && activeTab !== 'kb-management' && (
        <Tooltip title="HRMS Assistant" placement="left">
          <Fab
            color="primary"
            aria-label="Open HRMS Assistant"
            onClick={() => setChatOpen(true)}
            sx={{
              position: 'fixed', bottom: 28, right: 28, zIndex: 1300,
              width: 54, height: 54,
              background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)',
              boxShadow: '0 4px 20px rgba(21,101,192,0.45)',
              '&:hover': { background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)', transform: 'scale(1.06)' },
              transition: 'all 200ms cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            {/*<SmartToy sx={{ fontSize: 24 }} />*/}
            <DashboardCustomize sx={{ fontSize: 24 }} />
          </Fab>
        </Tooltip>
      )}

      <ChatbotDrawer
        open={chatOpen}
        onClose={() => { setChatOpen(false); setAiPrompt(null); }}
        userRoles={user?.roles || []}
        initialMessage={aiPrompt}
      />
    </Box>
    </ChatContext.Provider>
  );
}
